/**
 * Team chat presentation: per-user identity badges, grouping of other users'
 * chats by owner, and the sidebar collapse state kept in localStorage.
 */

import type { SelectOption } from '@/components/ui/select-state';
import type { ChatSession } from '@/services/api';

export interface TeamChatOwnerOption {
  key: string;
  ownerUsername: string;
}

/** The Person filter choices: "Everyone" (value "all") and then each owner. */
export function ownerFilterOptions(owners: TeamChatOwnerOption[]): SelectOption[] {
  return [
    { value: 'all', label: 'Everyone' },
    ...owners.map((owner) => ({ value: owner.key, label: owner.ownerUsername })),
  ];
}

/**
 * Badge backgrounds. Each one has at least 4.5:1 contrast with white text
 * (WCAG AA), so the initials stay readable. A badge always shows initials, so
 * color never carries the identity alone.
 *
 * Twelve hues 30 degrees apart (OKLCH), with alternate hues in a darker tier,
 * so no two colors look alike. The order steps 150 degrees, so consecutive
 * user ids get far-apart hues.
 */
export const OWNER_BADGE_COLORS = [
  '#de0776',
  '#0a622c',
  '#9a3cff',
  '#675102',
  '#0479b2',
  '#aa1004',
  '#128272',
  '#980393',
  '#677a0d',
  '#311dfd',
  '#ac6008',
  '#045d69',
] as const;

/** The badge color for a deleted owner. */
export const DELETED_OWNER_BADGE_COLOR = '#52525b';

export const OWNER_BADGE_TEXT_COLOR = '#ffffff';

/** A stable color for a user id. The same id always gets the same color. */
export function ownerBadgeColor(ownerUserId: number | null): string {
  if (ownerUserId === null) return DELETED_OWNER_BADGE_COLOR;
  const index = Math.abs(Math.trunc(ownerUserId)) % OWNER_BADGE_COLORS.length;
  return OWNER_BADGE_COLORS[index];
}

/** One or two uppercase initials from a username: "kevin.jordan" -> "KJ", "kevin" -> "KE". */
export function ownerInitials(username: string): string {
  const parts = username.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (parts.length === 0) return '?';
  const initials = parts.length > 1
    ? `${parts[0][0]}${parts[1][0]}`
    : parts[0].slice(0, 2);
  return initials.toUpperCase();
}

export interface TeamChatGroup {
  /** The owner user id as text, or "deleted" for chats whose owner was deleted. */
  key: string;
  ownerUserId: number | null;
  ownerUsername: string;
  sessions: ChatSession[];
  latestUpdatedAt: string;
}

export function teamChatGroupKey(session: Pick<ChatSession, 'ownerUserId'>): string {
  return session.ownerUserId === null ? 'deleted' : String(session.ownerUserId);
}

function byNewest(a: { updatedAt: string }, b: { updatedAt: string }): number {
  return b.updatedAt.localeCompare(a.updatedAt);
}

/** The user's own chats: the ones they can write to. */
export function ownChats(sessions: ChatSession[]): ChatSession[] {
  return sessions.filter((session) => session.canPost);
}

/**
 * Other users' chats grouped by owner. Chats are newest first in a group, and
 * the group with the latest activity comes first. `ownerKey` limits the result
 * to one group; "all" keeps every group.
 */
export function groupTeamChats(sessions: ChatSession[], ownerKey = 'all'): TeamChatGroup[] {
  const groups = new Map<string, TeamChatGroup>();
  for (const session of sessions) {
    if (session.canPost) continue;
    const key = teamChatGroupKey(session);
    if (ownerKey !== 'all' && key !== ownerKey) continue;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        ownerUserId: session.ownerUserId,
        ownerUsername: session.ownerUsername,
        sessions: [],
        latestUpdatedAt: session.updatedAt,
      };
      groups.set(key, group);
    }
    group.sessions.push(session);
    if (session.updatedAt > group.latestUpdatedAt) group.latestUpdatedAt = session.updatedAt;
  }
  const result = [...groups.values()];
  for (const group of result) group.sessions.sort(byNewest);
  return result.sort((a, b) => b.latestUpdatedAt.localeCompare(a.latestUpdatedAt));
}

export interface TeamChatCollapseState {
  teamCollapsed: boolean;
  collapsedGroups: string[];
}

export const TEAM_CHAT_COLLAPSE_STORAGE_KEY = 'telcoos.teamChats.collapse';

const DEFAULT_COLLAPSE_STATE: TeamChatCollapseState = { teamCollapsed: false, collapsedGroups: [] };

/** The saved collapse state. With no saved state, or no storage, every section is open. */
export function readTeamChatCollapseState(storage: Pick<Storage, 'getItem'> | undefined): TeamChatCollapseState {
  let saved: string | null = null;
  try {
    saved = storage?.getItem(TEAM_CHAT_COLLAPSE_STORAGE_KEY) ?? null;
  } catch {
    return DEFAULT_COLLAPSE_STATE;
  }
  if (!saved) return DEFAULT_COLLAPSE_STATE;
  try {
    const parsed = JSON.parse(saved) as Partial<TeamChatCollapseState>;
    return {
      teamCollapsed: parsed.teamCollapsed === true,
      collapsedGroups: Array.isArray(parsed.collapsedGroups)
        ? parsed.collapsedGroups.filter((key): key is string => typeof key === 'string')
        : [],
    };
  } catch {
    return DEFAULT_COLLAPSE_STATE;
  }
}

/** Saves the collapse state. A browser with storage disabled keeps it for this page only. */
export function writeTeamChatCollapseState(
  storage: Pick<Storage, 'setItem'> | undefined,
  state: TeamChatCollapseState
): void {
  try {
    storage?.setItem(TEAM_CHAT_COLLAPSE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Collapse state is a display preference; the in-memory state still applies.
  }
}

export function toggleCollapsedGroup(state: TeamChatCollapseState, key: string): TeamChatCollapseState {
  return {
    ...state,
    collapsedGroups: state.collapsedGroups.includes(key)
      ? state.collapsedGroups.filter((item) => item !== key)
      : [...state.collapsedGroups, key],
  };
}
